import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "./api";
import {
  Save, RotateCcw, Loader2, Check, Calendar as CalIcon,
  Clock, Ban, ChevronLeft, ChevronRight, AlertCircle, X, Plus, Lock,
} from "lucide-react";
import { formatSlot, mergeSlots } from "../lib/booking";

const WEEKDAYS = [
  { i: 0, short: "Mon", long: "Monday" },
  { i: 1, short: "Tue", long: "Tuesday" },
  { i: 2, short: "Wed", long: "Wednesday" },
  { i: 3, short: "Thu", long: "Thursday" },
  { i: 4, short: "Fri", long: "Friday" },
  { i: 5, short: "Sat", long: "Saturday" },
  { i: 6, short: "Sun", long: "Sunday" },
];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fmtLongDate = (d) =>
  d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

const monthLabel = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const dowMonFirst = (d) => (d.getDay() + 6) % 7;

// ---------- Sub-components ----------
const StatCard = ({ icon: Icon, label, value, hint, tone = "slate" }) => {
  const tones = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };
  return (
    <div className={`flex items-start gap-3 rounded-2xl border p-4 ${tones[tone]}`}>
      <span className={`w-9 h-9 rounded-full bg-white border border-current/20 flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] opacity-70">{label}</p>
        <p className="mt-1 font-serif text-2xl text-slate-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {value}
        </p>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
    </div>
  );
};

const WeekdayToggle = ({ w, isOpen, onToggle, slotsCount }) => (
  <button
    type="button"
    data-testid={`weekday-${w.short.toLowerCase()}`}
    onClick={onToggle}
    className={`group relative rounded-2xl border p-4 text-left transition-all ${
      isOpen
        ? "bg-white border-slate-200 hover:border-amber-400 hover:shadow-sm"
        : "bg-slate-900 border-slate-900 text-white"
    }`}
  >
    <p className={`text-[10px] uppercase tracking-[0.22em] ${isOpen ? "text-amber-700" : "text-amber-400"}`}>
      {w.short}
    </p>
    <p className={`mt-2 font-serif text-lg ${isOpen ? "text-slate-900" : "text-white"}`}
       style={{ fontFamily: "'Cormorant Garamond', serif" }}>
      {isOpen ? "Open" : "Closed"}
    </p>
    <p className={`mt-1 text-[11px] ${isOpen ? "text-slate-500" : "text-slate-300"}`}>
      {isOpen ? `${slotsCount} slots` : "All day blocked"}
    </p>
    <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${isOpen ? "bg-emerald-500" : "bg-rose-400"}`} />
  </button>
);

// ---------- Page ----------
function AddTimeForm({ allTimes, dateTimes, onAdd }) {
  const [value, setValue] = useState("");
  const [scope, setScope] = useState("all"); // "all" | "date"
  const [dateISO, setDateISO] = useState("");

  const existsForScope = value && (
    scope === "all"
      ? allTimes.includes(value)
      : dateISO && (allTimes.includes(value) || (dateTimes[dateISO] || []).includes(value))
  );
  const canAdd = value && !existsForScope && (scope === "all" || !!dateISO);

  const submit = () => {
    if (onAdd(value, scope === "date" ? dateISO : null)) setValue("");
  };

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex rounded-full border border-slate-200 bg-white p-1" data-testid="add-time-scope">
        <button
          type="button"
          data-testid="scope-all-days"
          onClick={() => setScope("all")}
          className={`px-3.5 py-1 rounded-full text-xs transition-colors ${
            scope === "all" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          Every day
        </button>
        <button
          type="button"
          data-testid="scope-one-date"
          onClick={() => setScope("date")}
          className={`px-3.5 py-1 rounded-full text-xs transition-colors ${
            scope === "date" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
          }`}
        >
          One date only
        </button>
      </div>
      <div className="flex items-center gap-2">
        {scope === "date" && (
          <input
            type="date"
            data-testid="add-time-date"
            value={dateISO}
            min={toISO(new Date())}
            onChange={(e) => setDateISO(e.target.value)}
            className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
          />
        )}
        <input
          type="time"
          data-testid="add-time-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400"
        />
        <button
          type="button"
          data-testid="add-time-btn"
          onClick={submit}
          disabled={!canAdd}
          className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm flex items-center gap-1.5 hover:bg-slate-700 disabled:opacity-40 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Add a time
        </button>
      </div>
      {existsForScope && <span className="text-xs text-rose-600">This time already exists{scope === "date" ? " on that date" : ""}.</span>}
    </div>
  );
}

export default function AdminAvailability() {
  const [data, setData] = useState({ blocked_weekdays: [], blocked_dates: [], blocked_times: {}, custom_times: [], custom_date_times: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [month, setMonth] = useState(() => {
    const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d;
  });
  const [selectedDate, setSelectedDate] = useState(null); // Date object or null

  useEffect(() => {
    adminApi.get("/availability")
      .then((r) => setData({
        blocked_weekdays: r.data.blocked_weekdays || [],
        blocked_dates: r.data.blocked_dates || [],
        blocked_times: r.data.blocked_times || {},
        custom_times: r.data.custom_times || [],
        custom_date_times: r.data.custom_date_times || {},
      }))
      .finally(() => setLoading(false));
  }, []);

  // Days grid for calendar
  const days = useMemo(() => {
    const first = new Date(month);
    const startOffset = (first.getDay() + 6) % 7; // Mon-first
    const firstShown = new Date(first); firstShown.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }).map((_, i) => {
      const d = new Date(firstShown); d.setDate(firstShown.getDate() + i); return d;
    });
  }, [month]);

  // ---------- Mutations ----------
  const markDirty = () => { setDirty(true); setSaved(false); };

  const ALL_TIMES = useMemo(() => mergeSlots(data.custom_times), [data.custom_times]);

  const addCustomTime = (t, iso = null) => {
    if (!t) return false;
    if (iso) {
      const dayTimes = mergeSlots([...data.custom_times, ...(data.custom_date_times[iso] || [])]);
      if (dayTimes.includes(t)) return false;
      markDirty();
      setData((s) => ({
        ...s,
        custom_date_times: {
          ...s.custom_date_times,
          [iso]: [...(s.custom_date_times[iso] || []), t].sort(),
        },
      }));
      return true;
    }
    if (ALL_TIMES.includes(t)) return false;
    markDirty();
    setData((s) => ({ ...s, custom_times: [...s.custom_times, t].sort() }));
    return true;
  };

  const removeCustomDateTime = (iso, t) => {
    markDirty();
    setData((s) => {
      const cdt = { ...s.custom_date_times };
      const kept = (cdt[iso] || []).filter((x) => x !== t);
      if (kept.length) cdt[iso] = kept; else delete cdt[iso];
      const bt = { ...s.blocked_times };
      if (bt[iso]) {
        const keptBlocked = bt[iso].filter((x) => x !== t);
        if (keptBlocked.length) bt[iso] = keptBlocked; else delete bt[iso];
      }
      return { ...s, custom_date_times: cdt, blocked_times: bt };
    });
  };

  const removeCustomTime = (t) => {
    markDirty();
    setData((s) => {
      const bt = {};
      Object.entries(s.blocked_times).forEach(([iso, slots]) => {
        const kept = slots.filter((x) => x !== t);
        if (kept.length) bt[iso] = kept;
      });
      return { ...s, custom_times: s.custom_times.filter((x) => x !== t), blocked_times: bt };
    });
  };

  const toggleWeekday = (i) => {
    markDirty();
    setData((s) => ({
      ...s,
      blocked_weekdays: s.blocked_weekdays.includes(i)
        ? s.blocked_weekdays.filter((x) => x !== i)
        : [...s.blocked_weekdays, i],
    }));
  };

  const toggleDateFull = (d) => {
    markDirty();
    const iso = toISO(d);
    setData((s) => {
      const isBlocked = s.blocked_dates.includes(iso);
      const next = { ...s };
      next.blocked_dates = isBlocked ? s.blocked_dates.filter((x) => x !== iso) : [...s.blocked_dates, iso];
      // If we're fully blocking, remove any partial slot exceptions for that date
      if (!isBlocked) {
        const bt = { ...s.blocked_times }; delete bt[iso]; next.blocked_times = bt;
      }
      return next;
    });
  };

  const toggleSlot = (d, slot) => {
    markDirty();
    const iso = toISO(d);
    setData((s) => {
      const bt = { ...s.blocked_times };
      const cur = new Set(bt[iso] || []);
      if (cur.has(slot)) cur.delete(slot); else cur.add(slot);
      if (cur.size === 0) delete bt[iso]; else bt[iso] = Array.from(cur).sort();
      return { ...s, blocked_times: bt };
    });
  };

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await adminApi.put("/availability", data);
      setSaved(true); setDirty(false);
      setTimeout(() => setSaved(false), 2200);
    } finally { setSaving(false); }
  };

  const reset = () => {
    markDirty();
    setData((s) => ({
      blocked_weekdays: [], blocked_dates: [], blocked_times: {},
      custom_times: s.custom_times, custom_date_times: s.custom_date_times,
    }));
  };

  // ---------- Helpers ----------
  const isWeekdayBlocked = (d) => data.blocked_weekdays.includes(dowMonFirst(d));
  const isDateFullyBlocked = (d) => data.blocked_dates.includes(toISO(d));
  const partialSlotsForDate = (d) => data.blocked_times[toISO(d)] || [];

  // Stats
  const stats = useMemo(() => ({
    daysOpen: 7 - data.blocked_weekdays.length,
    daysClosed: data.blocked_weekdays.length,
    blockedDates: data.blocked_dates.length,
    partialDates: Object.keys(data.blocked_times).filter((k) => !data.blocked_dates.includes(k)).length,
  }), [data]);

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;

  const selectedISO = selectedDate ? toISO(selectedDate) : null;
  const selectedDayTimes = selectedISO
    ? mergeSlots([...data.custom_times, ...(data.custom_date_times[selectedISO] || [])])
    : ALL_TIMES;
  const selectedFullyBlocked = selectedDate ? isDateFullyBlocked(selectedDate) : false;
  const selectedWeekdayClosed = selectedDate ? isWeekdayBlocked(selectedDate) : false;
  const selectedBlockedSlots = selectedDate ? partialSlotsForDate(selectedDate) : [];

  return (
    <div className="p-10 max-w-7xl mx-auto space-y-8" data-testid="admin-availability">
      {/* Header */}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-amber-700">availability</p>
          <h1 className="font-serif text-4xl text-slate-900 mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Calendar &amp; openings
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Set your weekly defaults, close specific dates, and disable individual time slots when needed.
            Changes apply instantly to the public booking modal.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={reset}
            data-testid="reset-availability"
            className="px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-700 flex items-center gap-2 hover:bg-slate-50"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Clear all
          </button>
          <button
            onClick={save}
            disabled={saving || !dirty}
            data-testid="save-availability"
            className={`px-5 py-2 rounded-full text-sm flex items-center gap-2 transition-colors ${
              saved ? "bg-emerald-600 text-white"
                    : dirty ? "bg-slate-900 text-white hover:bg-slate-800"
                            : "bg-slate-200 text-slate-500 cursor-not-allowed"
            }`}
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving</>
              : saved ? <><Check className="w-4 h-4" /> Saved</>
              : <><Save className="w-3.5 h-3.5" /> {dirty ? "Save changes" : "Saved"}</>}
          </button>
        </div>
      </header>

      {/* Stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={CalIcon}    label="Open weekdays"   value={`${stats.daysOpen} / 7`}  tone="emerald" />
        <StatCard icon={Ban}        label="Weekly closures" value={stats.daysClosed}          tone={stats.daysClosed > 0 ? "rose" : "slate"} />
        <StatCard icon={X}          label="Blocked dates"   value={stats.blockedDates}        tone={stats.blockedDates > 0 ? "rose" : "slate"} />
        <StatCard icon={Clock}      label="Slot exceptions" value={stats.partialDates}        hint="dates with partial blocks" tone={stats.partialDates > 0 ? "amber" : "slate"} />
      </section>

      {/* Weekly defaults */}
      <section className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="weekly-defaults">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">step 1</p>
            <h2 className="font-serif text-2xl text-slate-900 mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Weekly defaults
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">Close entire weekdays. Visible in the booking modal as fully greyed-out days.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {WEEKDAYS.map((w) => (
            <WeekdayToggle
              key={w.i}
              w={w}
              isOpen={!data.blocked_weekdays.includes(w.i)}
              onToggle={() => toggleWeekday(w.i)}
              slotsCount={ALL_TIMES.length}
            />
          ))}
        </div>
      </section>

      {/* Time slots */}
      <section className="bg-white rounded-2xl border border-slate-200 p-7" data-testid="time-slots-section">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">step 2</p>
            <h2 className="font-serif text-2xl text-slate-900 mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Time slots
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              The standard slots are always offered. Add extra times — for every day or a single date — and remove only the ones you added.
            </p>
          </div>
          <AddTimeForm allTimes={ALL_TIMES} dateTimes={data.custom_date_times} onAdd={addCustomTime} />
        </div>
        <div className="flex flex-wrap gap-2">
          {ALL_TIMES.map((t) => {
            const isCustom = data.custom_times.includes(t);
            return (
              <span
                key={t}
                data-testid={`timeslot-pill-${t}`}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${
                  isCustom
                    ? "bg-amber-50 border-amber-200 text-amber-900"
                    : "bg-slate-50 border-slate-200 text-slate-700"
                }`}
              >
                <Clock className="w-3.5 h-3.5 opacity-60" />
                {formatSlot(t)}
                {isCustom ? (
                  <button
                    type="button"
                    data-testid={`remove-timeslot-${t}`}
                    onClick={() => removeCustomTime(t)}
                    aria-label={`Remove ${formatSlot(t)}`}
                    className="w-5 h-5 rounded-full text-amber-700 hover:text-white hover:bg-rose-500 flex items-center justify-center transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <Lock className="w-3 h-3 text-slate-400" title="Standard slot — cannot be removed" />
                )}
              </span>
            );
          })}
        </div>
        {data.custom_times.length > 0 && (
          <p className="mt-4 text-xs text-amber-700 inline-flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> {data.custom_times.length} custom slot{data.custom_times.length > 1 ? "s" : ""} added — remember to save.
          </p>
        )}

        {Object.keys(data.custom_date_times).length > 0 && (
          <div className="mt-6 pt-5 border-t border-slate-100" data-testid="date-specific-times">
            <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-3">Date-specific additions</p>
            <div className="space-y-2">
              {Object.keys(data.custom_date_times).sort().map((iso) => (
                <div key={iso} className="flex items-center flex-wrap gap-2">
                  <span className="text-sm text-slate-700 font-medium min-w-[170px]">
                    {new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                  </span>
                  {data.custom_date_times[iso].map((t) => (
                    <span
                      key={t}
                      data-testid={`datetime-pill-${iso}-${t}`}
                      className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3.5 py-1.5 text-sm font-medium text-violet-900"
                    >
                      <Clock className="w-3 h-3 opacity-60" />
                      {formatSlot(t)}
                      <button
                        type="button"
                        data-testid={`remove-datetime-${iso}-${t}`}
                        onClick={() => removeCustomDateTime(iso, t)}
                        aria-label={`Remove ${formatSlot(t)} on ${iso}`}
                        className="w-5 h-5 rounded-full text-violet-700 hover:text-white hover:bg-rose-500 flex items-center justify-center transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Calendar + Date details */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="calendar-section">
        {/* Calendar */}
        <div className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">step 3</p>
              <h2 className="font-serif text-2xl text-slate-900 mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Date overrides
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMonth((m) => { const d = new Date(m); d.setMonth(d.getMonth() - 1); return d; })}
                data-testid="month-prev"
                className="w-8 h-8 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <p className="font-serif text-lg text-slate-900 min-w-[170px] text-center"
                 style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {monthLabel(month)}
              </p>
              <button
                onClick={() => setMonth((m) => { const d = new Date(m); d.setMonth(d.getMonth() + 1); return d; })}
                data-testid="month-next"
                className="w-8 h-8 rounded-full border border-slate-200 hover:bg-slate-50 flex items-center justify-center text-slate-600">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Day labels */}
          <div className="grid grid-cols-7 gap-1 text-[11px] text-slate-400 mb-2">
            {WEEKDAYS.map((w) => <div key={w.i} className="text-center uppercase tracking-wider">{w.short}</div>)}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === month.getMonth();
              const blockedDate = isDateFullyBlocked(d);
              const blockedDow = isWeekdayBlocked(d);
              const partial = !blockedDate && partialSlotsForDate(d).length > 0;
              const today = toISO(d) === toISO(new Date());
              const isSelected = selectedISO === toISO(d);
              return (
                <button
                  key={i}
                  data-testid={`day-${toISO(d)}`}
                  onClick={() => inMonth && setSelectedDate(new Date(d))}
                  disabled={!inMonth}
                  className={`aspect-square rounded-xl text-sm flex flex-col items-center justify-center relative transition-all ${
                    !inMonth ? "text-slate-300" :
                    blockedDate ? "bg-slate-900 text-white hover:bg-slate-800" :
                    blockedDow ? "bg-slate-50 text-slate-400 hover:bg-slate-100" :
                    partial ? "bg-amber-50 text-amber-900 hover:bg-amber-100 border border-amber-200" :
                    "bg-white border border-slate-200 text-slate-700 hover:border-amber-400"
                  } ${isSelected ? "ring-2 ring-amber-500 ring-offset-1" : today && !blockedDate ? "ring-1 ring-amber-400/60" : ""}`}
                >
                  <span className="font-medium">{d.getDate()}</span>
                  {partial && <span className="absolute bottom-1 w-1 h-1 rounded-full bg-amber-500" />}
                  {blockedDate && <span className="absolute bottom-1 text-[8px] uppercase tracking-wider">off</span>}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center flex-wrap gap-x-5 gap-y-2 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-slate-200" /> Open</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-50 border border-slate-200" /> Weekly closed</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> Partial slots blocked</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-slate-900" /> Fully blocked</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded ring-1 ring-amber-400 bg-white" /> Today</span>
          </div>
        </div>

        {/* Date details panel */}
        <aside className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-7" data-testid="date-details">
          {!selectedDate ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-14 text-slate-400">
              <CalIcon className="w-10 h-10 mb-3 text-slate-300" />
              <p className="text-sm">Select a date in the calendar</p>
              <p className="text-xs mt-1">to manage its specific slots.</p>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">step 4</p>
                  <h2 className="font-serif text-xl text-slate-900 mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {fmtLongDate(selectedDate)}
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Close date panel"
                  data-testid="close-date-panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Weekday closed notice */}
              {selectedWeekdayClosed && !selectedFullyBlocked && (
                <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600 flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                  <span>This weekday is closed by default ({WEEKDAYS[dowMonFirst(selectedDate)].long}s). The date is unavailable regardless of slot settings below.</span>
                </div>
              )}

              {/* Block entire day */}
              <button
                onClick={() => toggleDateFull(selectedDate)}
                data-testid="toggle-full-day"
                className={`mt-5 w-full rounded-xl border px-4 py-3 flex items-center justify-between transition-all ${
                  selectedFullyBlocked
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white border-slate-200 hover:border-rose-400"
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Ban className={`w-4 h-4 ${selectedFullyBlocked ? "text-rose-400" : "text-slate-500"}`} />
                  Block entire day
                </span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${
                  selectedFullyBlocked ? "bg-rose-500 justify-end" : "bg-slate-200 justify-start"
                }`}>
                  <span className="w-4 h-4 rounded-full bg-white shadow" />
                </span>
              </button>

              {/* Slot grid */}
              <div className={`mt-6 ${selectedFullyBlocked ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">individual slots</p>
                  <p className="text-[11px] text-slate-400">
                    {selectedDayTimes.length - selectedBlockedSlots.length} / {selectedDayTimes.length} open
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {selectedDayTimes.map((t) => {
                    const blocked = selectedBlockedSlots.includes(t);
                    return (
                      <button
                        key={t}
                        onClick={() => toggleSlot(selectedDate, t)}
                        data-testid={`slot-${t}`}
                        className={`relative px-3 py-3 rounded-xl border text-sm font-medium transition-all ${
                          blocked
                            ? "bg-rose-50 border-rose-200 text-rose-700 line-through"
                            : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400 hover:bg-emerald-50/40"
                        }`}
                      >
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3 opacity-60" />
                          {formatSlot(t)}
                        </span>
                        {blocked && <span className="block text-[9px] uppercase tracking-wider mt-0.5">blocked</span>}
                      </button>
                    );
                  })}
                </div>

                {selectedBlockedSlots.length === 0 ? (
                  <p className="mt-4 text-xs text-emerald-700 inline-flex items-center gap-1.5">
                    <Check className="w-3 h-3" /> All slots are open on this date.
                  </p>
                ) : (
                  <p className="mt-4 text-xs text-rose-700 inline-flex items-center gap-1.5">
                    <Ban className="w-3 h-3" /> {selectedBlockedSlots.length} slot{selectedBlockedSlots.length > 1 ? "s" : ""} blocked.
                  </p>
                )}
              </div>
            </>
          )}
        </aside>
      </section>

      {/* Save reminder */}
      {dirty && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-5 py-3 rounded-full shadow-xl flex items-center gap-3 text-sm" data-testid="unsaved-banner">
          <AlertCircle className="w-4 h-4 text-amber-400" />
          You have unsaved changes
          <button onClick={save} disabled={saving} className="ml-2 px-4 py-1.5 rounded-full bg-amber-500 text-slate-900 font-medium text-xs hover:bg-amber-400">
            {saving ? "Saving…" : "Save now"}
          </button>
        </div>
      )}
    </div>
  );
}
